import { computed, onScopeDispose, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { useFirmaRemota } from '../composables/useFirmaRemota'
import type { Sessione } from '../types'

/**
 * Sessione di firma dell'utente (un solo OTP per più documenti). Il server tiene la chiave di
 * sessione: qui ci sono solo l'identificativo opaco e la scadenza, e SOLO in memoria (niente
 * persistenza, niente storage). L'OTP non passa mai da questo store: arriva come argomento di
 * `apri`, viene inoltrato e dimenticato.
 */
export const useFirmaSessioneStore = defineStore('firmaSessione', () => {
  const api = useFirmaRemota()
  const sessione = ref<Sessione | null>(null)
  const adesso = ref(Date.now())
  let timer: ReturnType<typeof setInterval> | null = null

  const scadenzaMs = computed(() => (sessione.value?.scadeIl ? Date.parse(sessione.value.scadeIl) : 0))
  const secondiRimasti = computed(() => Math.max(0, Math.ceil((scadenzaMs.value - adesso.value) / 1000)))
  const sessioneId = computed(() => sessione.value?.sessioneId ?? null)
  const firmaId = computed(() => sessione.value?.firmaId)
  const attiva = computed(() => !!sessioneId.value && secondiRimasti.value > 0)
  /** «m:ss» per il conto alla rovescia. */
  const restante = computed(() => {
    const s = secondiRimasti.value
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
  })

  function ferma(): void {
    if (timer) { clearInterval(timer); timer = null }
  }
  function avvia(): void {
    ferma()
    adesso.value = Date.now()
    timer = setInterval(() => {
      adesso.value = Date.now()
      if (sessione.value && adesso.value >= scadenzaMs.value) azzera()
    }, 1000)
  }
  watch(sessione, (s) => { if (s) avvia(); else ferma() })
  onScopeDispose(ferma)

  function azzera(): void {
    sessione.value = null
  }

  /** Rilegge lo stato dal server (`GET /sessione`): 404 = nessuna sessione. */
  async function carica(): Promise<void> {
    try {
      sessione.value = await api.sessioneCorrente()
    } catch {
      sessione.value = null
    }
  }

  async function apri(firmaId: number | undefined, dispositivoId: number, otp: string): Promise<string> {
    const s = await api.apriSessione(firmaId, dispositivoId, otp)
    sessione.value = s
    return s.sessioneId ?? ''
  }

  async function chiudi(): Promise<void> {
    const id = sessioneId.value
    azzera()
    if (id) {
      try { await api.chiudiSessione(id) } catch { /* già scaduta o chiusa */ }
    }
  }

  return { sessione, sessioneId, firmaId, attiva, secondiRimasti, restante, carica, apri, chiudi, azzera }
})

/**
 * Da chiamare al logout e al cambio di utente: la sessione di firma è della persona (il server la lega a utente e firma, ma
 * il browser non deve mostrare né riusare l'identificativo e il conto alla rovescia di chi c'era prima). Non chiama il server:
 * il logout ha già invalidato la sessione di login, e la sessione di firma scade da sola.
 */
export function dimenticaSessioneFirma(): void {
  useFirmaSessioneStore().azzera()
}
